import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SEO from "../../../components/SEO/SEO";
import PublicHeader from "../../../components/PublicHeader/PublicHeader";
import {
  ApiError,
  checkCommunityNavigatorAvailability,
  CommunityNavigatorAvailability,
  submitCommunityNavigatorRequest,
} from "../../../api/communityNavigator";
import "./CommunityNavigatorHelpPage.css";

type FieldErrors = {
  postalCode?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phone?: string;
  email?: string;
  consent?: string;
};

type Outcome = "submitted" | "already-active" | "availability-changed";

const FIND_DOCTOR_PATH = "/public/find-family-doctor";
const TEMPORARY_CHECK_ERROR = "We couldn't check availability right now. Please try again.";
const TEMPORARY_SUBMIT_ERROR = "We couldn't submit your request right now. Please try again.";
const RATE_LIMIT_ERROR = "Too many requests. Please try again later.";

function formatPostalCodeInput(raw: string): string {
  const compact = String(raw || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 6);
  return compact.length <= 3 ? compact : `${compact.slice(0, 3)} ${compact.slice(3)}`;
}

function isValidPostalCode(value: string): boolean {
  return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(value.toUpperCase().replace(/\s/g, ""));
}

function isValidDateOfBirth(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return false;
  }
  const today = new Date();
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return year >= 1900 && date.getTime() <= todayUtc;
}

function looksLikePhone(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

function isValidOptionalEmail(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return trimmed.length <= 320 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

function readIncomingPostalCode(state: unknown): string {
  if (!state || typeof state !== "object" || Array.isArray(state)) return "";
  const postalCode = (state as { postalCode?: unknown }).postalCode;
  if (typeof postalCode !== "string") return "";
  const formatted = formatPostalCodeInput(postalCode);
  return isValidPostalCode(formatted) ? formatted : "";
}

function todayInputValue(): string {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

function focusField(id: string) {
  window.setTimeout(() => {
    document.getElementById(id)?.focus();
  }, 0);
}

function safeApiMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.status === 429) return RATE_LIMIT_ERROR;
  if (error instanceof ApiError && error.status === 400 && error.message) return error.message;
  return fallback;
}

function fieldErrorsFromMessage(message: string): FieldErrors {
  const text = message.toLowerCase();
  const errors: FieldErrors = {};
  if (text.includes("first") && text.includes("name")) errors.firstName = "Enter your first name.";
  if (text.includes("last name") || (text.includes("first") && text.includes("last"))) {
    errors.lastName = "Enter your last name.";
  }
  if (text.includes("date of birth") || text.includes("birth")) {
    errors.dateOfBirth = "Enter a valid date of birth.";
  }
  if (text.includes("phone")) errors.phone = "Enter a valid phone number.";
  if (text.includes("email")) errors.email = "Enter a valid email address.";
  if (text.includes("postal")) errors.postalCode = "Enter a valid Canadian postal code.";
  if (text.includes("consent") || text.includes("agree")) {
    errors.consent = "You must agree before submitting this request.";
  }
  return errors;
}

export default function CommunityNavigatorHelpPage() {
  const location = useLocation();
  const checkLock = useRef(false);
  const submitLock = useRef(false);
  const postalInputRef = useRef<HTMLInputElement>(null);

  const [postalCode, setPostalCode] = useState(() => readIncomingPostalCode(location.state));
  const [availability, setAvailability] = useState<CommunityNavigatorAvailability | null>(null);
  const [confirmedPostalCode, setConfirmedPostalCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(false);

  useEffect(() => {
    const incoming = readIncomingPostalCode(location.state);
    if (!incoming) return;
    setPostalCode(incoming);
    setAvailability(null);
    setConfirmedPostalCode("");
    setOutcome(null);
    setPageError(null);
    setFieldErrors({});
  }, [location.state]);

  const available = availability?.available === true;
  const unavailable = availability?.available === false;
  const ohtName =
    availability?.available === true && availability.ohtName ? availability.ohtName.trim() : "";

  async function onCheckAvailability(event: FormEvent) {
    event.preventDefault();
    if (checkLock.current) return;

    setPageError(null);
    if (!isValidPostalCode(postalCode)) {
      setFieldErrors({ postalCode: "Enter a valid Canadian postal code." });
      setAvailability(null);
      focusField("cnh-postal");
      return;
    }

    checkLock.current = true;
    setChecking(true);
    setFieldErrors({});
    try {
      const result = await checkCommunityNavigatorAvailability(postalCode.trim());
      if (result?.available === true) {
        setAvailability(result);
        setConfirmedPostalCode(postalCode.trim());
        setOutcome(null);
      } else if (result?.available === false) {
        setAvailability({ available: false });
        setConfirmedPostalCode("");
      } else {
        setAvailability(null);
        setPageError(TEMPORARY_CHECK_ERROR);
      }
    } catch (error) {
      setAvailability(null);
      setPageError(safeApiMessage(error, TEMPORARY_CHECK_ERROR));
      if (error instanceof ApiError && error.status === 400) {
        setFieldErrors({ postalCode: "Enter a valid Canadian postal code." });
      }
    } finally {
      checkLock.current = false;
      setChecking(false);
    }
  }

  function onChangePostalCode() {
    setAvailability(null);
    setConfirmedPostalCode("");
    setPageError(null);
    setFieldErrors({});
    setOutcome(null);
    window.setTimeout(() => postalInputRef.current?.focus(), 0);
  }

  async function onSubmitRequest(event: FormEvent) {
    event.preventDefault();
    if (submitLock.current || !available) return;

    const errors: FieldErrors = {};
    if (!firstName.trim()) errors.firstName = "Enter your first name.";
    if (!lastName.trim()) errors.lastName = "Enter your last name.";
    if (!isValidDateOfBirth(dateOfBirth)) errors.dateOfBirth = "Enter a valid date of birth.";
    if (!looksLikePhone(phone)) errors.phone = "Enter a valid phone number.";
    if (!isValidOptionalEmail(email)) errors.email = "Enter a valid email address.";
    if (!isValidPostalCode(confirmedPostalCode)) {
      errors.postalCode = "Enter a valid Canadian postal code.";
    }
    if (!consentAccepted) errors.consent = "You must agree before submitting this request.";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setPageError("Please check the information you entered and try again.");
      const firstId = errors.firstName
        ? "cnh-first-name"
        : errors.lastName
          ? "cnh-last-name"
          : errors.dateOfBirth
            ? "cnh-dob"
            : errors.phone
              ? "cnh-phone"
              : errors.email
                ? "cnh-email"
                : errors.consent
                  ? "cnh-consent"
                  : "cnh-form-error";
      focusField(firstId);
      return;
    }

    submitLock.current = true;
    setSubmitting(true);
    setPageError(null);
    setFieldErrors({});
    try {
      const trimmedEmail = email.trim();
      const result = await submitCommunityNavigatorRequest({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth,
        postalCode: confirmedPostalCode,
        phone: phone.trim(),
        ...(trimmedEmail ? { email: trimmedEmail } : {}),
        consentAccepted: true,
      });

      if (result?.available === false) {
        setOutcome("availability-changed");
        setAvailability({ available: false });
        return;
      }
      if (result?.alreadyActive) {
        setOutcome("already-active");
        return;
      }
      if (result?.success === true) {
        setOutcome("submitted");
        return;
      }
      setPageError(TEMPORARY_SUBMIT_ERROR);
    } catch (error) {
      const message = safeApiMessage(error, TEMPORARY_SUBMIT_ERROR);
      setPageError(message);
      if (error instanceof ApiError && error.status === 400) {
        setFieldErrors(fieldErrorsFromMessage(message));
      }
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  }

  return (
    <div className="cnh-page">
      <SEO
        title="Get help finding a family doctor"
        description="Check whether Community Navigator support is available in your area, then submit a request for help finding a family doctor."
        noIndex={true}
      />
      <PublicHeader variant="solid" />

      <main className="cnh-container">
        <header className="cnh-card cnh-intro">
          <h1>Get help finding a family doctor</h1>
          <p>Community Navigator support may be available in your area. Enter your postal code to check.</p>
        </header>

        {outcome === "submitted" ? (
          <section className="cnh-card cnh-status" aria-live="polite">
            <h2>Your request has been submitted</h2>
            <p>A Community Navigator may contact you about next steps.</p>
            <p>Submitting a request does not guarantee attachment to a family doctor.</p>
          </section>
        ) : null}

        {outcome === "already-active" ? (
          <section className="cnh-card cnh-status" aria-live="polite">
            <h2>Your request is already being handled</h2>
            <p>We already have an active Community Navigator request for this patient.</p>
            <Link className="cnh-btn" to={FIND_DOCTOR_PATH}>
              Find a family doctor
            </Link>
          </section>
        ) : null}

        {outcome === "availability-changed" ? (
          <section className="cnh-card cnh-status" aria-live="polite">
            <h2>Community Navigator support is not currently available in your area.</h2>
            <p>Availability may have changed since you checked your postal code.</p>
            <p>You can still search for family doctors accepting new patients through AcceptingMD.</p>
            <Link className="cnh-btn cnh-btn-primary" to={FIND_DOCTOR_PATH}>
              Find a family doctor
            </Link>
          </section>
        ) : null}

        {outcome ? null : (
          <>
            {available ? (
              <section className="cnh-card cnh-status" aria-live="polite">
                <h2>Community Navigator support is available in your area.</h2>
                <p>
                  A Community Navigator can contact you to learn more about what you need and help identify
                  participating family doctor options.
                </p>
                {ohtName ? <p className="cnh-oht">Community Navigator service: {ohtName}</p> : null}
              </section>
            ) : (
              <form className="cnh-card cnh-form" onSubmit={onCheckAvailability} noValidate>
                <div className="cnh-field">
                  <label className="cnh-label" htmlFor="cnh-postal">
                    Postal code
                  </label>
                  <input
                    ref={postalInputRef}
                    id="cnh-postal"
                    className={`cnh-input${fieldErrors.postalCode ? " cnh-input-invalid" : ""}`}
                    type="text"
                    name="postalCode"
                    value={postalCode}
                    disabled={checking}
                    onChange={(event) => {
                      setPostalCode(formatPostalCodeInput(event.target.value));
                      setAvailability(null);
                      setFieldErrors((current) => ({ ...current, postalCode: undefined }));
                    }}
                    placeholder="L6M 5P7"
                    autoComplete="postal-code"
                    inputMode="text"
                    aria-invalid={fieldErrors.postalCode ? true : undefined}
                    aria-describedby={fieldErrors.postalCode ? "cnh-postal-error" : undefined}
                  />
                  {fieldErrors.postalCode ? (
                    <p id="cnh-postal-error" className="cnh-field-error">
                      {fieldErrors.postalCode}
                    </p>
                  ) : null}
                </div>

                {pageError ? (
                  <p id="cnh-form-error" className="cnh-alert" role="alert">
                    {pageError}
                  </p>
                ) : null}

                <button className="cnh-btn cnh-btn-primary" type="submit" disabled={checking}>
                  {checking ? "Checking availability…" : "Check availability"}
                </button>
              </form>
            )}

            {unavailable ? (
              <section className="cnh-card cnh-status" aria-live="polite">
                <h2>Community Navigator support is not currently available in your area.</h2>
                <p>You can still search for family doctors accepting new patients through AcceptingMD.</p>
                <Link className="cnh-btn cnh-btn-primary" to={FIND_DOCTOR_PATH}>
                  Find a family doctor
                </Link>
              </section>
            ) : null}

            {available ? (
              <form className="cnh-card cnh-form" onSubmit={onSubmitRequest} noValidate>
                {pageError ? (
                  <p id="cnh-form-error" className="cnh-alert" role="alert">
                    {pageError}
                  </p>
                ) : null}

                <div className="cnh-grid">
                  <div className="cnh-field">
                    <label className="cnh-label" htmlFor="cnh-first-name">
                      First name
                    </label>
                    <input
                      id="cnh-first-name"
                      className={`cnh-input${fieldErrors.firstName ? " cnh-input-invalid" : ""}`}
                      type="text"
                      name="firstName"
                      value={firstName}
                      disabled={submitting}
                      onChange={(event) => setFirstName(event.target.value)}
                      autoComplete="given-name"
                      aria-invalid={fieldErrors.firstName ? true : undefined}
                      aria-describedby={fieldErrors.firstName ? "cnh-first-name-error" : undefined}
                    />
                    {fieldErrors.firstName ? (
                      <p id="cnh-first-name-error" className="cnh-field-error">
                        {fieldErrors.firstName}
                      </p>
                    ) : null}
                  </div>

                  <div className="cnh-field">
                    <label className="cnh-label" htmlFor="cnh-last-name">
                      Last name
                    </label>
                    <input
                      id="cnh-last-name"
                      className={`cnh-input${fieldErrors.lastName ? " cnh-input-invalid" : ""}`}
                      type="text"
                      name="lastName"
                      value={lastName}
                      disabled={submitting}
                      onChange={(event) => setLastName(event.target.value)}
                      autoComplete="family-name"
                      aria-invalid={fieldErrors.lastName ? true : undefined}
                      aria-describedby={fieldErrors.lastName ? "cnh-last-name-error" : undefined}
                    />
                    {fieldErrors.lastName ? (
                      <p id="cnh-last-name-error" className="cnh-field-error">
                        {fieldErrors.lastName}
                      </p>
                    ) : null}
                  </div>

                  <div className="cnh-field">
                    <label className="cnh-label" htmlFor="cnh-dob">
                      Date of birth
                    </label>
                    <input
                      id="cnh-dob"
                      className={`cnh-input${fieldErrors.dateOfBirth ? " cnh-input-invalid" : ""}`}
                      type="date"
                      name="dateOfBirth"
                      value={dateOfBirth}
                      max={todayInputValue()}
                      disabled={submitting}
                      onChange={(event) => setDateOfBirth(event.target.value)}
                      autoComplete="bday"
                      aria-invalid={fieldErrors.dateOfBirth ? true : undefined}
                      aria-describedby={fieldErrors.dateOfBirth ? "cnh-dob-error" : undefined}
                    />
                    {fieldErrors.dateOfBirth ? (
                      <p id="cnh-dob-error" className="cnh-field-error">
                        {fieldErrors.dateOfBirth}
                      </p>
                    ) : null}
                  </div>

                  <div className="cnh-field">
                    <label className="cnh-label" htmlFor="cnh-phone">
                      Phone number
                    </label>
                    <input
                      id="cnh-phone"
                      className={`cnh-input${fieldErrors.phone ? " cnh-input-invalid" : ""}`}
                      type="tel"
                      name="phone"
                      value={phone}
                      disabled={submitting}
                      onChange={(event) => setPhone(event.target.value)}
                      autoComplete="tel"
                      inputMode="tel"
                      aria-invalid={fieldErrors.phone ? true : undefined}
                      aria-describedby={fieldErrors.phone ? "cnh-phone-error" : undefined}
                    />
                    {fieldErrors.phone ? (
                      <p id="cnh-phone-error" className="cnh-field-error">
                        {fieldErrors.phone}
                      </p>
                    ) : null}
                  </div>

                  <div className="cnh-field">
                    <label className="cnh-label" htmlFor="cnh-email">
                      Email (optional)
                    </label>
                    <input
                      id="cnh-email"
                      className={`cnh-input${fieldErrors.email ? " cnh-input-invalid" : ""}`}
                      type="email"
                      name="email"
                      value={email}
                      disabled={submitting}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      inputMode="email"
                      aria-invalid={fieldErrors.email ? true : undefined}
                      aria-describedby={fieldErrors.email ? "cnh-email-error" : undefined}
                    />
                    {fieldErrors.email ? (
                      <p id="cnh-email-error" className="cnh-field-error">
                        {fieldErrors.email}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="cnh-confirmed-postal">
                  <div>
                    <div className="cnh-label">Postal code</div>
                    <div className="cnh-postal-value">{confirmedPostalCode}</div>
                  </div>
                  <button className="cnh-btn" type="button" onClick={onChangePostalCode} disabled={submitting}>
                    Change postal code
                  </button>
                </div>
                {fieldErrors.postalCode ? <p className="cnh-field-error">{fieldErrors.postalCode}</p> : null}

                <div className="cnh-field">
                  <div className={`cnh-consent${fieldErrors.consent ? " cnh-consent-invalid" : ""}`}>
                    <input
                      id="cnh-consent"
                      type="checkbox"
                      checked={consentAccepted}
                      disabled={submitting}
                      onChange={(event) => setConsentAccepted(event.target.checked)}
                      aria-invalid={fieldErrors.consent ? true : undefined}
                      aria-describedby={fieldErrors.consent ? "cnh-consent-error" : undefined}
                    />
                    <label htmlFor="cnh-consent">
                      I confirm that I am submitting this request voluntarily and that the information provided is
                      accurate. If I am submitting this request for someone else, I confirm that I am authorized to do
                      so. By submitting this request, I consent to AcceptingMD and the Community Navigator team serving
                      my area collecting, using, and disclosing the information I provide to process and verify this
                      family doctor attachment request, send secure status updates, and allow a Community Navigator to
                      contact me about the request and next steps. I understand that submitting a request does not
                      guarantee attachment to a family doctor and that I should not include detailed medical information
                      in this form. I agree to the{" "}
                      <Link to="/public/privacy" onClick={(event) => event.stopPropagation()}>
                        Privacy Policy
                      </Link>{" "}
                      and{" "}
                      <Link to="/public/terms" onClick={(event) => event.stopPropagation()}>
                        Terms of Use
                      </Link>
                      .
                    </label>
                  </div>
                  {fieldErrors.consent ? (
                    <p id="cnh-consent-error" className="cnh-field-error">
                      {fieldErrors.consent}
                    </p>
                  ) : null}
                </div>

                <button className="cnh-btn cnh-btn-primary cnh-btn-full" type="submit" disabled={submitting}>
                  {submitting ? "Submitting…" : "Submit request"}
                </button>
              </form>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
